import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationsAPI, QUERY_KEYS } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Building2, Plus, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { extractPaginatedData } from '@/utils/pagination';
import { useDebouncedValue } from '@/utils/useDebouncedValue';

export default function OrganizationsPage() {
  const perPage = 10;
  const { hasRole } = useAuth();
  const canManageOrgs = hasRole('super_admin');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingOrg, setEditingOrg] = useState<any>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const queryClient = useQueryClient();

  const { data: orgsData, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.organizations(), debouncedSearch, page],
    queryFn: () => organizationsAPI.list({ page, per_page: perPage, search: debouncedSearch }),
    enabled: canManageOrgs,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => organizationsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.organizations() });
      setShowForm(false);
      toast.success('Организация успешно создана');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => organizationsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.organizations() });
      setEditingOrg(null);
      toast.success('Организация успешно обновлена');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => organizationsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.organizations() });
      toast.success('Организация удалена');
    },
  });

  const { items: organizations, meta } = extractPaginatedData<any>(orgsData, page, perPage);

  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить организацию? Все связанные данные будут удалены!')) {
      deleteMutation.mutate(id);
    }
  };

  if (!canManageOrgs) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">У вас нет прав доступа к этой странице</p>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Организации</h1>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Добавить организацию
        </Button>
      </div>

      {/* Search */}
      <Input
        type="search"
        placeholder="Поиск по названию..."
        value={search}
        onChange={(e) => {
          setPage(1);
          setSearch(e.target.value);
        }}
      />

      <Modal
        isOpen={showForm || !!editingOrg}
        onClose={() => {
          setShowForm(false);
          setEditingOrg(null);
        }}
        title={editingOrg ? 'Редактировать организацию' : 'Добавить организацию'}
      >
        <OrganizationForm
          initialData={editingOrg}
          onSubmit={async (data) => {
            if (editingOrg) {
              await updateMutation.mutateAsync({ id: editingOrg.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingOrg(null);
          }}
        />
      </Modal>

      {/* Organizations Table */}
      <Card>
        <CardContent>
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">#</th>
                  <th className="text-left py-3 px-4 font-semibold">Название</th>
                  <th className="text-left py-3 px-4 font-semibold">Slug</th>
                  <th className="text-left py-3 px-4 font-semibold">Филиалы</th>
                  <th className="text-left py-3 px-4 font-semibold">Пользователи</th>
                  <th className="text-left py-3 px-4 font-semibold">Статус</th>
                  <th className="text-right py-3 px-4 font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {organizations.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      Нет организаций
                    </td>
                  </tr>
                ) : (
                  organizations.map((org: any) => (
                    <tr key={org.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">{org.id}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-500" />
                          <span className="font-medium">{org.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {org.slug}
                        </code>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="secondary">{org.branches_count || 0}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="secondary">{org.users_count || 0}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={org.is_active ? 'success' : 'secondary'}>
                          {org.is_active ? 'Активна' : 'Неактивна'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingOrg(org)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(org.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  );
}

// Organization Form Component
function OrganizationForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    code: initialData?.code || '',
    slug: initialData?.slug || '',
    timezone: initialData?.timezone || 'Asia/Tashkent',
    currency: initialData?.currency || 'UZS',
    is_active: initialData?.is_active ?? true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">
          Название организации <span className="text-red-500">*</span>
        </label>
        <Input
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="HappyTel LLC"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Код (необязательно)
        </label>
        <Input
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
          placeholder="HAPPYTEL (сгенерируется автоматически)"
          maxLength={10}
        />
        <p className="text-xs text-gray-500 mt-1">Оставьте пустым для автогенерации</p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Slug (URL identifier)
        </label>
        <Input
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          placeholder="happytel-llc"
        />
        <p className="text-xs text-gray-500 mt-1">
          Оставьте пустым для автогенерации
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Часовой пояс <span className="text-red-500">*</span>
        </label>
        <select
          required
          value={formData.timezone}
          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg"
        >
          <option value="Asia/Tashkent">Asia/Tashkent (UTC+5)</option>
          <option value="Asia/Samarkand">Asia/Samarkand (UTC+5)</option>
          <option value="Asia/Dubai">Asia/Dubai (UTC+4)</option>
          <option value="Europe/Moscow">Europe/Moscow (UTC+3)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">
          Валюта <span className="text-red-500">*</span>
        </label>
        <select
          required
          value={formData.currency}
          onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg"
        >
          <option value="UZS">UZS - Узбекский сум</option>
          <option value="USD">USD - Доллар США</option>
          <option value="EUR">EUR - Евро</option>
          <option value="RUB">RUB - Российский рубль</option>
        </select>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          className="w-4 h-4"
        />
        <label htmlFor="is_active" className="text-sm font-medium">
          Активная организация
        </label>
      </div>

      <div className="flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Отмена
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </div>
    </form>
  );
}


